/**
 * LIS presentation helpers — pure functions shared across screens (no React, no I/O). Health
 * derivation, label maps, and small formatters. Status semantics are color + glyph + text everywhere
 * (daltonisme + médico-légal); the glyph/tone live here, the text comes from i18n.
 */
import type { BadgeTone } from "@/ui";
import type { Automate, EnAttenteMotif, Protocole } from "./types";

/** A device health verdict — drives the dashboard badge + filtering. */
export type Health = "actif" | "inactif" | "attention" | "absent";

export interface HealthDescriptor {
  health: Health;
  tone: BadgeTone;
  /** i18n key for the label text. */
  labelKey: string;
}

/**
 * Derive a device's health (NOVA-021). When the connection plane reports a LIVE link-state, that is the
 * authoritative signal: `connecte` → actif (success); `degrade` (incl. a stale/silent connecte, surfaced
 * server-side as `effective_state`) → attention (warn, with the degrade reason); `deconnecte` → absent
 * (the link dropped). When there is NO live link-state (a non-local box, or the plane has never reported
 * this analyzer), we fall back to the honest config-completeness verdict: a raccordé, actif analyzer with
 * a present, active config is "actif"; missing config is "absent"; an inactive statut or disabled config
 * is "inactif". Tone is paired with a glyph + text everywhere (daltonisme + médico-légal).
 */
export function deviceHealth(a: Automate): HealthDescriptor {
  const live = a.link_state;
  if (live) {
    const state = live.effective_state ?? live.state;
    if (state === "connecte") return { health: "actif", tone: "success", labelKey: "lis.health.connecte" };
    if (state === "degrade") return { health: "attention", tone: "warn", labelKey: "lis.health.degrade" };
    if (state === "deconnecte") return { health: "absent", tone: "danger", labelKey: "lis.health.deconnecte" };
  }
  // No live link-state — config-completeness fallback (the honest verdict without the connection plane).
  if (!a.config) return { health: "absent", tone: "warn", labelKey: "lis.health.absent" };
  if (a.statut !== "actif" || !a.config.actif)
    return { health: "inactif", tone: "neutral", labelKey: "lis.health.inactif" };
  return { health: "actif", tone: "success", labelKey: "lis.health.actif" };
}

/** Protocol → short human badge (the family label). */
export const PROTOCOLE_LABEL: Record<Protocole, string> = {
  "astm-e1394": "ASTM E1394",
  "hl7v2-oru": "HL7 v2 ORU",
  poct1a: "POCT1-A",
};

/** Quarantine reason → badge tone (warn/danger; never color-only — paired with a glyph + text). */
export function motifTone(motif: EnAttenteMotif | string): BadgeTone {
  switch (motif) {
    case "discordance_grave":
      return "danger";
    case "orphelin":
      return "warn";
    case "code_inconnu":
    default:
      return "info";
  }
}

/** Discipline code → a readable FR label (the catalog disciplines). */
export const DISCIPLINE_LABEL: Record<string, string> = {
  biochimie: "Biochimie",
  immunoanalyse: "Immunoanalyse",
  hematologie: "Hématologie",
  coagulation: "Coagulation",
  gaz_du_sang: "Gaz du sang",
  hba1c: "HbA1c",
  urines: "Urines",
  elisa: "ELISA",
  GEN: "Général",
};

export function disciplineLabel(code: string): string {
  return DISCIPLINE_LABEL[code] ?? code;
}

/** A stable id → string (BigInt ids cross the wire as strings; tolerate numbers). */
export function idStr(id: string | number | null | undefined): string {
  return id == null ? "" : String(id);
}

/** Short relative-ish timestamp for the monitor (local, mono-friendly). */
export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
